import {
  AlertCircle,
  File as FileIcon,
  Loader2,
  RotateCw,
  Upload,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useDropzone, type Accept, type FileRejection } from "react-dropzone";
import { toast } from "sonner";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/Button";
import {
  isImageFile,
  rotateImageFile,
  rotateImageFromUrl,
} from "@/shared/utils/image.utils";

type SelectedStateProps = {
  file: File | null
  previewUrl: string | null
  removeFile: (event?: MouseEvent<HTMLButtonElement>) => void
  success: boolean
  successMessage?: string
  isLoading: boolean
  removeButtonLabel: string
  fileSizeLabel: string | null
  selectedIcon: ReactNode
  disabled: boolean
};

type FileDropzoneProps = {
  title?: string
  subtitle?: string
  dragTitle?: string
  logo?: ReactNode
  selectedFile?: File | null
  previewUrl?: string | null
  onChange?: (file: File | null) => void
  onDropRejected?: (fileRejections: FileRejection[]) => void
  accept?: Accept
  maxSize?: number
  multiple?: boolean
  disabled?: boolean
  isLoading?: boolean
  loadingText?: string
  error?: string | null
  success?: boolean
  successMessage?: string
  removeButtonLabel?: string
  selectedIcon?: ReactNode
  className?: string
  dropzoneClassName?: string
  formatFileSize?: (size: number) => string
  renderSelectedState?: (props: SelectedStateProps) => ReactNode
};

const DEFAULT_REMOVE_LABEL = "Supprimer";
const formatFileSizeDefault = (bytes: number): string => {
  if (!bytes) return "0 Bytes";
  const units = ["Bytes", "KB", "MB", "GB"];
  const rawSize = Math.floor(Math.log(bytes) / Math.log(1024));
  const sizeIndex = Math.min(rawSize, units.length - 1);
  const normalized = bytes / Math.pow(1024, sizeIndex);
  return `${normalized.toFixed(normalized >= 10 || sizeIndex === 0 ? 0 : 2)} ${units[sizeIndex]}`;
};

export const FileDropzone = ({
  title = "Déposez ou cliquez pour ajouter",
  subtitle,
  dragTitle = "Déposez votre fichier ici...",
  logo,
  selectedFile,
  previewUrl,
  onChange,
  onDropRejected,
  accept,
  maxSize,
  multiple = false,
  disabled = false,
  isLoading = false,
  loadingText = "Upload en cours...",
  error,
  success = false,
  successMessage,
  removeButtonLabel = DEFAULT_REMOVE_LABEL,
  selectedIcon,
  className,
  dropzoneClassName,
  formatFileSize = formatFileSizeDefault,
  renderSelectedState,
}: FileDropzoneProps) => {
  const isControlled = selectedFile !== undefined;
  const [internalFile, setInternalFile] = useState<File | null>(null);
  const [rejectionError, setRejectionError] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [previewLoadFailed, setPreviewLoadFailed] = useState(false);

  const file = isControlled ? (selectedFile ?? null) : internalFile;

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file?.type?.startsWith("image/")) {
      setObjectUrl(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  // Local blob URL takes precedence: the remote previewUrl can race
  // ActiveStorage variant processing right after a fresh upload.
  const effectivePreviewUrl = objectUrl ?? previewUrl;

  useEffect(() => {
    setPreviewLoadFailed(false);
  }, [effectivePreviewUrl]);
  const effectiveError = error ?? rejectionError;
  const showSuccessState = success && !effectiveError && !isLoading;

  const setFile = (nextFile: File | null) => {
    if (!isControlled) {
      setInternalFile(nextFile);
    }
    onChange?.(nextFile);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    const nextFile = acceptedFiles[0];
    if (!nextFile) return;
    setRejectionError(null);
    setFile(nextFile);
  };

  const handleDropRejected = (fileRejections: FileRejection[]) => {
    onDropRejected?.(fileRejections);

    const rejection = fileRejections[0];
    if (!rejection) return;

    const fileTooBig = rejection.errors?.some((err) => err.code === "file-too-large") ?? false;
    const invalidType = rejection.errors?.some((err) => err.code === "file-invalid-type") ?? false;

    if (fileTooBig) {
      setRejectionError("Le fichier est trop volumineux.");
    }
    else if (invalidType) {
      setRejectionError("Type de fichier non autorisé.");
    }
    else {
      setRejectionError("Erreur lors de la sélection du fichier.");
    }
  };

  const handleRemove = (event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    setRejectionError(null);
    setFile(null);
  };

  const handleRotate = async (event?: MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    if (isRotating || disabled || isLoading) return;
    setIsRotating(true);
    try {
      if (file && isImageFile(file)) {
        setFile(await rotateImageFile(file, 90));
      }
      else if (!file && previewUrl) {
        setFile(await rotateImageFromUrl(previewUrl, "image", 90));
      }
    }
    catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de pivoter l'image.";
      toast.error("Rotation impossible", { description: message });
    }
    finally {
      setIsRotating(false);
    }
  };

  const selectedStateContent = useMemo(() => {
    if (!file && !effectivePreviewUrl) return null;

    const context: SelectedStateProps = {
      file,
      previewUrl: effectivePreviewUrl ?? null,
      removeFile: handleRemove,
      success: showSuccessState,
      successMessage,
      isLoading,
      removeButtonLabel,
      fileSizeLabel: file ? formatFileSize(file.size) : null,
      selectedIcon: selectedIcon ?? <FileIcon className="w-8 h-8 text-green-600" />,
      disabled,
    };

    if (renderSelectedState) {
      return renderSelectedState(context);
    }

    const canRotate = !disabled && !isLoading && (
      (file && isImageFile(file)) || (!file && Boolean(context.previewUrl))
    );

    return (
      <div className="flex flex-col items-center space-y-4 p-6 w-full">
        {context.previewUrl && !previewLoadFailed && (
          <div className="relative w-full flex justify-center">
            <img
              src={context.previewUrl}
              alt={file?.name ?? "File preview"}
              className="max-h-48 rounded-md object-contain"
              onError={() => setPreviewLoadFailed(true)}
            />
            {canRotate && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={isRotating}
                onClick={handleRotate}
                aria-label="Pivoter l'image de 90°"
                title="Pivoter de 90°"
                className="absolute bottom-2 right-2 h-8 w-8 p-0 shadow-md bg-white/95 hover:bg-white"
              >
                {isRotating
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <RotateCw className="h-4 w-4" />}
              </Button>
            )}
          </div>
        )}
        {(!context.previewUrl || previewLoadFailed) && (
          <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
            {context.selectedIcon}
          </div>
        )}
        <div className="space-y-2 text-center">
          {file?.name && (
            <p className="text-xs font-medium text-gray-900">
              {file?.name}
            </p>
          )}
          {context.fileSizeLabel && (
            <p className="text-sm text-gray-500">
              {context.fileSizeLabel}
            </p>
          )}
          {context.success && context.successMessage && (
            <p className="text-sm text-green-600 font-medium">
              {`✅ ${context.successMessage}`}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRemove}
          disabled={disabled}
          className="flex items-center space-x-2"
        >
          <X className="w-4 h-4" />
          <span>{removeButtonLabel}</span>
        </Button>
      </div>
    );
  }, [disabled, effectivePreviewUrl, file, formatFileSize, isLoading, isRotating, previewLoadFailed, removeButtonLabel, renderSelectedState, selectedIcon, showSuccessState, successMessage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    accept,
    maxSize,
    multiple,
    disabled: disabled || isLoading,
  });

  return (
    <div className={cn("w-full", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "relative flex flex-col justify-center items-center w-full min-h-[200px] border-2 border-dashed bg-white rounded-lg text-center transition-all duration-200 overflow-hidden",
          dropzoneClassName,
          {
            "border-primary bg-primary/10": isDragActive,
            "border-primary/30 hover:border-primary":
              !isDragActive && !effectiveError && !showSuccessState,
            "border-red-500 bg-red-50": Boolean(effectiveError),
            "border-green-500 bg-green-50": showSuccessState,
            "cursor-pointer bg-slate-50": !disabled && !isLoading,
            "cursor-not-allowed opacity-60": disabled || isLoading,
          },
        )}
      >
        <input {...getInputProps()} />

        {isLoading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="flex flex-col items-center space-y-2">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-600">{loadingText}</p>
            </div>
          </div>
        )}

        {!file && !effectivePreviewUrl && !isLoading && (
          <div className="flex flex-col items-center space-y-4 p-6">
            <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full">
              {logo ?? <Upload className="w-8 h-8 text-gray-400" />}
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900">
                {isDragActive ? dragTitle : title}
              </p>
              {subtitle && (
                <p className="text-sm text-gray-500">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        )}

        {(file || effectivePreviewUrl) && !isLoading && selectedStateContent}
      </div>

      {effectiveError && (
        <div className="mt-3 flex items-center space-x-2 text-red-600">
          <AlertCircle className="w-4 h-4" />
          <p className="text-sm">{effectiveError}</p>
        </div>
      )}

      {showSuccessState && !effectiveError && successMessage && (
        <div className="mt-3 flex items-center space-x-2 text-green-600">
          <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full" />
          </div>
          <p className="text-sm">{successMessage}</p>
        </div>
      )}
    </div>
  );
};

export default FileDropzone;
